// ReportsPage — Análisis › Reportes.
//
// Tabla diaria de órdenes con todos los campos operativos clave para
// el supervisor: cliente, chofer/placa, montos, estado, motivo de
// rechazo y hora de entrega. Acciones:
//   - Exportar a CSV (Excel abre CSV nativo) o XLS via abuso del
//     content-type (Excel acepta HTML como hoja de cálculo).
//   - Ver gráfico resumen del día (desglose por estado + top vehículos
//     por entrega efectiva).
//
// Estrategia de datos: combinamos `listOrders({plannedDate})` + las
// rutas del día (para resolver chofer/placa) + el detalle de cada
// ruta (para extraer delivery.delivered_total/delivered_at y
// exception.reason_label). Cacheamos por fecha en estado local; el
// usuario invalida pidiendo "Refrescar".

import { useEffect, useMemo, useState } from "react";
import { Button, Card, Input } from "../components/ui";
import { api } from "../data/api";
import type {
  Order,
  RouteStopRow,
  User,
} from "../data/schemaTypes";
import { useMastersStore } from "../data/mastersStore";

type ReportRow = {
  orderId: string;
  orderCode: string;
  clientName: string;
  clientCode: string;
  driverName: string;
  driverCode: string;
  plate: string;
  totalAmount: number;
  // Lo que efectivamente se cobró/entregó del POD (parciales o entregas
  // completas). Si la orden no se atendió todavía: undefined.
  deliveredAmount?: number;
  status: string;
  // Motivo de rechazo si status = failed/skipped. Texto legible.
  rejectionReason?: string;
  // Hora de entrega o de registro de excepción (ISO local).
  attendedAt?: string;
};

const STATUS_LABEL: Record<string, string> = {
  pending: "Pendiente",
  assigned: "Asignada",
  in_progress: "En ruta",
  delivered: "Entregada",
  partial: "Entrega parcial",
  failed: "Rechazada",
  skipped: "Omitida",
  completed: "Completada",
  cancelled: "Cancelada",
};

const STATUS_COLOR: Record<string, string> = {
  delivered: "#2E7D32",
  partial: "#F9A825",
  failed: "#C62828",
  skipped: "#C62828",
  pending: "#90A4AE",
  in_progress: "#1565C0",
  assigned: "#1565C0",
  completed: "#2E7D32",
  cancelled: "#90A4AE",
};

export function ReportsPage() {
  const todayISO = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  const [date, setDate] = useState<string>(todayISO);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showChart, setShowChart] = useState(false);

  // Masters cargados globalmente — los usamos para resolver nombres de
  // clientes/choferes/vehículos sin re-fetch.
  const clients = useMastersStore((s) => s.clients);
  const vehicles = useMastersStore((s) => s.vehicles);
  const drivers = useMastersStore((s) => s.users);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await buildReport(date, clients, vehicles, drivers);
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date, clients, vehicles, drivers]);

  // Filtrado client-side por search libre (busca en code, cliente,
  // chofer, placa). El operador típicamente busca un cliente en
  // particular o todas las órdenes de un chofer.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.orderCode.toLowerCase().includes(q) ||
        r.clientName.toLowerCase().includes(q) ||
        r.clientCode.toLowerCase().includes(q) ||
        r.driverName.toLowerCase().includes(q) ||
        r.plate.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const totals = useMemo(() => buildTotals(visible), [visible]);

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-ink">Reportes — Órdenes del día</h1>
          <p className="text-xs text-sub mt-0.5">
            {visible.length} de {rows.length} órdenes ·{" "}
            <span className="font-mono">{date}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-44"
          />
          <Input
            placeholder="Buscar cliente, chofer, placa…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
          />
          <Button
            variant="secondary"
            onClick={() => exportCsv(visible, date)}
            disabled={visible.length === 0}
          >
            Exportar CSV
          </Button>
          <Button
            variant="secondary"
            onClick={() => exportExcel(visible, date)}
            disabled={visible.length === 0}
          >
            Exportar Excel
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowChart((x) => !x)}
            disabled={visible.length === 0}
          >
            {showChart ? "Ocultar gráfico" : "Ver gráfico"}
          </Button>
        </div>
      </header>

      {showChart && (
        <Card padding="md">
          <DailyChart rows={visible} totals={totals} />
        </Card>
      )}

      {/* Strip de totales — el chofer/admin tiene siempre los counts
          arriba antes de ver la tabla. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        <SummaryTile label="Total órdenes" value={String(totals.count)} />
        <SummaryTile label="Entregadas" value={String(totals.delivered)} accent="#2E7D32" />
        <SummaryTile label="Parciales" value={String(totals.partial)} accent="#F9A825" />
        <SummaryTile label="Rechazadas" value={String(totals.failed)} accent="#C62828" />
        <SummaryTile
          label="Monto entregado"
          value={`S/ ${formatMoney(totals.deliveredAmount)}`}
        />
      </div>

      <Card padding="none">
        {loading ? (
          <div className="p-6 text-center text-sub text-sm">Cargando reporte…</div>
        ) : error ? (
          <div className="p-6 text-center text-state-failed text-sm">{error}</div>
        ) : visible.length === 0 ? (
          <div className="p-6 text-center text-sub text-sm">
            Sin órdenes para esta fecha.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase text-sub bg-page sticky top-0">
                <tr className="text-left">
                  <Th>Orden</Th>
                  <Th>Cliente</Th>
                  <Th>Chofer</Th>
                  <Th>Placa</Th>
                  <Th align="right">Monto</Th>
                  <Th align="right">Entregado</Th>
                  <Th>Estado</Th>
                  <Th>Motivo rechazo</Th>
                  <Th>Atendida</Th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr
                    key={r.orderId}
                    className={i % 2 === 0 ? "bg-card" : "bg-page/40"}
                  >
                    <Td mono>{r.orderCode}</Td>
                    <Td>
                      <div className="text-ink">{r.clientName || "—"}</div>
                      {r.clientCode && (
                        <div className="font-mono text-[10px] text-sub">
                          {r.clientCode}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <div className="text-ink">{r.driverName || "—"}</div>
                      {r.driverCode && (
                        <div className="font-mono text-[10px] text-sub">
                          {r.driverCode}
                        </div>
                      )}
                    </Td>
                    <Td mono>{r.plate}</Td>
                    <Td align="right">S/ {formatMoney(r.totalAmount)}</Td>
                    <Td align="right">
                      {r.deliveredAmount != null
                        ? `S/ ${formatMoney(r.deliveredAmount)}`
                        : "—"}
                    </Td>
                    <Td>
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold"
                        style={{
                          color: STATUS_COLOR[r.status] ?? "#5C6B7A",
                          backgroundColor:
                            (STATUS_COLOR[r.status] ?? "#5C6B7A") + "1A",
                        }}
                      >
                        {STATUS_LABEL[r.status] ?? r.status}
                      </span>
                    </Td>
                    <Td>
                      <span className="text-state-failed text-xs">
                        {r.rejectionReason ?? ""}
                      </span>
                    </Td>
                    <Td mono>
                      {r.attendedAt ? formatDateTime(r.attendedAt) : "—"}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------
// Construcción del reporte — orquesta los fetches y arma las filas.
// ---------------------------------------------------------------------

async function buildReport(
  date: string,
  clients: { id: string; name: string; code: string }[],
  vehicles: { id: string; plate: string }[],
  drivers: User[],
): Promise<ReportRow[]> {
  // Orders del día. listOrders soporta planned_date pero por las dudas
  // filtramos también del lado del cliente.
  const orders = await api.listOrders({ plannedDate: date, limit: 1000 });
  const ordersForDate = orders.filter((o) => o.planned_date === date);
  if (ordersForDate.length === 0) return [];

  // Routes del día (ventana ±1 para tolerar UTC drift; filtramos exacto).
  const window = dateWindow(date);
  const routes = await api.listRoutes({
    fromDate: window.from,
    toDate: window.to,
    limit: 500,
  });
  const sameDay = routes.filter((r) => r.planned_date === date);

  // Fan-out a /api/routes/{id} para extraer stops con POD denormalizado.
  const details = await Promise.all(
    sameDay.map(async (r) => {
      try {
        return await api.getRoute(r.id);
      } catch {
        return null;
      }
    }),
  );

  // Map order_id → datos del stop (POD + ruta a la que pertenece).
  type StopWithRoute = {
    stop: RouteStopRow;
    routeDriverId?: string;
    routeVehicleId?: string;
  };
  const stopByOrderId = new Map<string, StopWithRoute>();
  for (const route of details) {
    if (!route) continue;
    for (const s of route.stops ?? []) {
      stopByOrderId.set(s.order_id, {
        stop: s,
        routeDriverId: route.driver_user_id ?? undefined,
        routeVehicleId: route.vehicle_id ?? undefined,
      });
    }
  }

  const clientById = new Map(clients.map((c) => [c.id, c]));
  const vehicleById = new Map(vehicles.map((v) => [v.id, v]));
  const driverById = new Map(drivers.map((d) => [d.id, d]));

  return ordersForDate.map((o) => buildRow(o, stopByOrderId, clientById, vehicleById, driverById));
}

function buildRow(
  o: Order,
  stopByOrderId: Map<string, {
    stop: RouteStopRow;
    routeDriverId?: string;
    routeVehicleId?: string;
  }>,
  clientById: Map<string, { id: string; name: string; code: string }>,
  vehicleById: Map<string, { id: string; plate: string }>,
  driverById: Map<string, User>,
): ReportRow {
  const stopInfo = stopByOrderId.get(o.id);
  const stop = stopInfo?.stop;
  const driver = stopInfo?.routeDriverId
    ? driverById.get(stopInfo.routeDriverId)
    : undefined;
  const vehicle = stopInfo?.routeVehicleId
    ? vehicleById.get(stopInfo.routeVehicleId)
    : undefined;
  const client = clientById.get(o.client_id);
  // Estado efectivo: si el stop tiene status final, lo usamos en lugar
  // del status de orders (que puede quedar como "assigned" si el
  // backend no propaga). Garantiza consistencia con SupervisorPage.
  const effectiveStatus = stop?.status ?? o.status;
  return {
    orderId: o.id,
    orderCode: o.code,
    // Prioridad: wire denormalizado (cubre el caso de clients que no
    // están en el store local porque excedían el limit) → store →
    // stop denormalizado.
    clientName: o.client_name ?? client?.name ?? stop?.client_name ?? "",
    clientCode: client?.code ?? stop?.client_code ?? "",
    driverName: driver?.full_name ?? "",
    driverCode: driver?.code ?? "",
    plate: vehicle?.plate ?? "",
    totalAmount: o.total_amount,
    deliveredAmount: stop?.delivery?.delivered_total,
    status: effectiveStatus,
    rejectionReason:
      stop?.exception?.reason_label ?? stop?.exception?.reason_code,
    attendedAt:
      stop?.delivery?.delivered_at ?? stop?.exception?.occurred_at,
  };
}

function dateWindow(date: string): { from: string; to: string } {
  const ref = new Date(date + "T00:00:00Z");
  const prev = new Date(ref.getTime() - 86_400_000).toISOString().slice(0, 10);
  const next = new Date(ref.getTime() + 86_400_000).toISOString().slice(0, 10);
  return { from: prev, to: next };
}

// ---------------------------------------------------------------------
// Totales + gráfico
// ---------------------------------------------------------------------

type Totals = {
  count: number;
  delivered: number;
  partial: number;
  failed: number;
  pending: number;
  totalAmount: number;
  deliveredAmount: number;
  byVehicle: Array<{ plate: string; delivered: number; failed: number; pending: number }>;
};

function buildTotals(rows: ReportRow[]): Totals {
  let delivered = 0, partial = 0, failed = 0, pending = 0;
  let totalAmount = 0, deliveredAmount = 0;
  const perVehicle = new Map<string, { delivered: number; failed: number; pending: number }>();
  for (const r of rows) {
    totalAmount += r.totalAmount;
    deliveredAmount += r.deliveredAmount ?? 0;
    if (r.status === "delivered") delivered++;
    else if (r.status === "partial") partial++;
    else if (r.status === "failed" || r.status === "skipped") failed++;
    else pending++;
    const key = r.plate || "(sin placa)";
    const cur = perVehicle.get(key) ?? { delivered: 0, failed: 0, pending: 0 };
    if (r.status === "delivered" || r.status === "partial") cur.delivered++;
    else if (r.status === "failed" || r.status === "skipped") cur.failed++;
    else cur.pending++;
    perVehicle.set(key, cur);
  }
  const byVehicle = [...perVehicle.entries()]
    .map(([plate, c]) => ({ plate, ...c }))
    .sort((a, b) => b.delivered - a.delivered);
  return {
    count: rows.length,
    delivered,
    partial,
    failed,
    pending,
    totalAmount,
    deliveredAmount,
    byVehicle,
  };
}

/// Gráfico resumen del día — sin librería externa, SVG inline.
/// - Barra apilada arriba con la distribución de estados.
/// - Tabla simple con barras por vehículo (ranking de entregas).
function DailyChart({ rows, totals }: { rows: ReportRow[]; totals: Totals }) {
  const total = Math.max(totals.count, 1);
  const segments = [
    { label: "Entregadas", value: totals.delivered, color: "#2E7D32" },
    { label: "Parciales", value: totals.partial, color: "#F9A825" },
    { label: "Rechazadas", value: totals.failed, color: "#C62828" },
    { label: "Pendientes", value: totals.pending, color: "#90A4AE" },
  ];
  const maxByVehicle = Math.max(
    1,
    ...totals.byVehicle.map((v) => v.delivered + v.failed + v.pending),
  );
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-wide text-sub mb-2">
          Distribución por estado ({rows.length} órdenes)
        </div>
        <div className="flex h-6 rounded overflow-hidden border border-border">
          {segments.map((s) =>
            s.value > 0 ? (
              <div
                key={s.label}
                style={{
                  width: `${(s.value / total) * 100}%`,
                  backgroundColor: s.color,
                }}
                title={`${s.label}: ${s.value}`}
              />
            ) : null,
          )}
        </div>
        <div className="flex flex-wrap gap-3 mt-2 text-xs">
          {segments.map((s) => (
            <span key={s.label} className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: s.color }}
              />
              <span className="text-ink">{s.label}</span>
              <span className="text-sub">{s.value}</span>
              <span className="text-sub">
                ({((s.value / total) * 100).toFixed(1)}%)
              </span>
            </span>
          ))}
        </div>
      </div>

      {totals.byVehicle.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-sub mb-2">
            Por vehículo
          </div>
          <ul className="space-y-1.5">
            {totals.byVehicle.map((v) => {
              const sum = v.delivered + v.failed + v.pending;
              const pctOk = (v.delivered / sum) * 100;
              return (
                <li key={v.plate} className="flex items-center gap-2 text-xs">
                  <span className="font-mono font-semibold text-ink w-20 shrink-0">
                    {v.plate}
                  </span>
                  <div
                    className="flex h-3 rounded overflow-hidden border border-border flex-1"
                    style={{ maxWidth: `${(sum / maxByVehicle) * 100}%` }}
                  >
                    {v.delivered > 0 && (
                      <div
                        style={{
                          width: `${(v.delivered / sum) * 100}%`,
                          backgroundColor: "#2E7D32",
                        }}
                        title={`Entregadas ${v.delivered}`}
                      />
                    )}
                    {v.failed > 0 && (
                      <div
                        style={{
                          width: `${(v.failed / sum) * 100}%`,
                          backgroundColor: "#C62828",
                        }}
                        title={`Rechazadas ${v.failed}`}
                      />
                    )}
                    {v.pending > 0 && (
                      <div
                        style={{
                          width: `${(v.pending / sum) * 100}%`,
                          backgroundColor: "#90A4AE",
                        }}
                        title={`Pendientes ${v.pending}`}
                      />
                    )}
                  </div>
                  <span className="text-sub tabular-nums whitespace-nowrap">
                    {v.delivered}/{sum} · {pctOk.toFixed(0)}%
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Export — CSV y "Excel" (HTML que Excel abre como hoja).
// ---------------------------------------------------------------------

const HEADERS = [
  "Orden",
  "Cliente",
  "Código cliente",
  "Chofer",
  "Código chofer",
  "Placa",
  "Monto",
  "Monto entregado",
  "Estado",
  "Motivo rechazo",
  "Fecha de entrega",
];

function rowAsArray(r: ReportRow): string[] {
  return [
    r.orderCode,
    r.clientName,
    r.clientCode,
    r.driverName,
    r.driverCode,
    r.plate,
    r.totalAmount.toFixed(2),
    r.deliveredAmount != null ? r.deliveredAmount.toFixed(2) : "",
    STATUS_LABEL[r.status] ?? r.status,
    r.rejectionReason ?? "",
    r.attendedAt ? formatDateTime(r.attendedAt) : "",
  ];
}

function exportCsv(rows: ReportRow[], date: string) {
  const csvEscape = (s: string) => {
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [
    HEADERS.join(","),
    ...rows.map((r) => rowAsArray(r).map(csvEscape).join(",")),
  ];
  // BOM para que Excel abra el CSV como UTF-8 y no rompa los acentos.
  const blob = new Blob(["﻿" + lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  download(blob, `reporte-${date}.csv`);
}

function exportExcel(rows: ReportRow[], date: string) {
  // El truco viejo de "HTML con MIME excel + extensión .xls" funciona
  // pero Excel moderno (Office 2016+) muestra una advertencia molesta
  // "el formato del archivo y la extensión no coinciden" y, en algunos
  // builds, abre cada celda como string sin tipos. Generamos un XLSX
  // real (ZIP con XMLs) — el formato mínimo que cumple OOXML. Sin
  // dependencias externas (xlsx-lib pesa ~600KB).
  const sheetRows = [HEADERS, ...rows.map(rowAsArray)];
  // El Uint8Array<ArrayBufferLike> que devuelve buildXlsxBlob no es
  // asignable directo a BlobPart en strict mode (TS no garantiza que
  // sea ArrayBuffer puro vs SharedArrayBuffer). Lo envolvemos en
  // ArrayBuffer explícito copiando el slice subyacente.
  const bytes = buildXlsxBlob(sheetRows, "Reporte");
  const buffer = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  download(blob, `reporte-${date}.xlsx`);
}

// =====================================================================
// XLSX writer mínimo (sin dependencias). Crea un ZIP con la estructura
// OOXML básica: workbook + worksheet + relationships. Todas las celdas
// se escriben como inlineStr (texto inline) salvo cuando el valor es
// numérico (números con punto decimal y sin caracteres no-dígito);
// esos van como type number para que Excel los autosume.
// =====================================================================

function buildXlsxBlob(rows: string[][], sheetName: string): Uint8Array {
  const sheetXml = buildSheetXml(rows);
  const workbookXml = buildWorkbookXml(sheetName);
  const workbookRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`;
  const rootRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;
  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;

  const files: ZipEntry[] = [
    { name: "[Content_Types].xml", data: contentTypesXml },
    { name: "_rels/.rels", data: rootRelsXml },
    { name: "xl/workbook.xml", data: workbookXml },
    { name: "xl/_rels/workbook.xml.rels", data: workbookRelsXml },
    { name: "xl/worksheets/sheet1.xml", data: sheetXml },
  ];
  return buildZip(files);
}

function buildWorkbookXml(sheetName: string): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>
<sheet name="${xmlEscape(sheetName)}" sheetId="1" r:id="rId1"/>
</sheets>
</workbook>`;
}

function buildSheetXml(rows: string[][]): string {
  const xmlRows = rows
    .map((row, rIdx) => {
      const cells = row
        .map((value, cIdx) => buildCell(rIdx + 1, cIdx, value))
        .join("");
      return `<row r="${rIdx + 1}">${cells}</row>`;
    })
    .join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetData>${xmlRows}</sheetData>
</worksheet>`;
}

const NUM_RE = /^-?\d+(\.\d+)?$/;

function buildCell(rowNum: number, colIdx: number, value: string): string {
  const ref = `${colLetters(colIdx)}${rowNum}`;
  if (value === "") return `<c r="${ref}"/>`;
  if (NUM_RE.test(value)) {
    return `<c r="${ref}"><v>${value}</v></c>`;
  }
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlEscape(value)}</t></is></c>`;
}

function colLetters(idx: number): string {
  // 0 → A, 25 → Z, 26 → AA, …
  let n = idx;
  let s = "";
  while (true) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
    if (n < 0) break;
  }
  return s;
}

function xmlEscape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

// =====================================================================
// ZIP writer mínimo (stored, sin compresión). Suficiente para XLSXs
// chicos — reportes de unas decenas de filas pesan unos KB. Si en el
// futuro hay reportes de >1MB conviene introducir compresión deflate
// (o usar la lib `fflate`, ~16KB minificada).
// =====================================================================

type ZipEntry = { name: string; data: string };

function buildZip(entries: ZipEntry[]): Uint8Array {
  const encoder = new TextEncoder();
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;
  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name);
    const dataBytes = encoder.encode(entry.data);
    const crc = crc32(dataBytes);
    // Local file header
    const local = new Uint8Array(30 + nameBytes.length + dataBytes.length);
    const dv = new DataView(local.buffer);
    dv.setUint32(0, 0x04034b50, true); // signature
    dv.setUint16(4, 20, true);         // version needed
    dv.setUint16(6, 0, true);          // flags
    dv.setUint16(8, 0, true);          // method = stored
    dv.setUint16(10, 0, true);         // mtime
    dv.setUint16(12, 0, true);         // mdate
    dv.setUint32(14, crc, true);
    dv.setUint32(18, dataBytes.length, true);
    dv.setUint32(22, dataBytes.length, true);
    dv.setUint16(26, nameBytes.length, true);
    dv.setUint16(28, 0, true);         // extra field length
    local.set(nameBytes, 30);
    local.set(dataBytes, 30 + nameBytes.length);
    localParts.push(local);

    // Central directory header
    const central = new Uint8Array(46 + nameBytes.length);
    const dv2 = new DataView(central.buffer);
    dv2.setUint32(0, 0x02014b50, true);
    dv2.setUint16(4, 20, true);
    dv2.setUint16(6, 20, true);
    dv2.setUint16(8, 0, true);
    dv2.setUint16(10, 0, true);
    dv2.setUint16(12, 0, true);
    dv2.setUint16(14, 0, true);
    dv2.setUint32(16, crc, true);
    dv2.setUint32(20, dataBytes.length, true);
    dv2.setUint32(24, dataBytes.length, true);
    dv2.setUint16(28, nameBytes.length, true);
    dv2.setUint16(30, 0, true);
    dv2.setUint16(32, 0, true);
    dv2.setUint16(34, 0, true);
    dv2.setUint16(36, 0, true);
    dv2.setUint32(38, 0, true);
    dv2.setUint32(42, offset, true);
    central.set(nameBytes, 46);
    centralParts.push(central);

    offset += local.length;
  }
  const centralSize = centralParts.reduce((a, c) => a + c.length, 0);
  const centralOffset = offset;
  // End of central directory record
  const end = new Uint8Array(22);
  const dvE = new DataView(end.buffer);
  dvE.setUint32(0, 0x06054b50, true);
  dvE.setUint16(4, 0, true);
  dvE.setUint16(6, 0, true);
  dvE.setUint16(8, entries.length, true);
  dvE.setUint16(10, entries.length, true);
  dvE.setUint32(12, centralSize, true);
  dvE.setUint32(16, centralOffset, true);
  dvE.setUint16(20, 0, true);

  const total =
    localParts.reduce((a, p) => a + p.length, 0) + centralSize + 22;
  const out = new Uint8Array(total);
  let pos = 0;
  for (const p of localParts) {
    out.set(p, pos);
    pos += p.length;
  }
  for (const c of centralParts) {
    out.set(c, pos);
    pos += c.length;
  }
  out.set(end, pos);
  return out;
}

// CRC32 estándar, tabla precalculada.
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    t[i] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className="px-2 py-2 font-semibold border-b border-border"
      style={{ textAlign: align ?? "left" }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  mono,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
}) {
  return (
    <td
      className={`px-2 py-1.5 align-top ${mono ? "font-mono text-xs" : ""}`}
      style={{ textAlign: align ?? "left" }}
    >
      {children}
    </td>
  );
}

function SummaryTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <div className="bg-card border border-border rounded px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-sub">{label}</div>
      <div
        className="text-lg font-semibold tabular-nums"
        style={{ color: accent ?? "var(--ink, #243B53)" }}
      >
        {value}
      </div>
    </div>
  );
}

function formatMoney(n: number): string {
  return n.toLocaleString("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/// Fecha + hora en formato compacto local (es-PE), p.ej.
/// "11/06/2026 14:32:05". Usado en la tabla y en el CSV/Excel para que
/// el supervisor vea cuándo se atendió cada orden, no solo la hora.
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.toLocaleDateString("es-PE");
  const hhmm = d.toLocaleTimeString("es-PE", { hour12: false });
  return `${day} ${hhmm}`;
}
